import i18next from 'i18next';
import { AppProps } from '../../../@types/AppProps';
import { Valuation } from '../../../@types/Valuation';
import { ValuationState } from '../../../@types/ValuationState';
import ButtonArrowRight from '../../../Components/Button/ButtonArrowRight';
import HtmlNode from '../../../Components/Extension/HtmlNode';
import InputField from '../../../Components/Input/InputField';
import InputSelect from '../../../Components/Input/InputSelect';
import InputTextarea from '../../../Components/Input/InputTextarea';
import createLead from '../../../Http/createLead';
import Alert from '../../../Templates/Alert';
import { createLeadBody } from './utils';
import validation from './validation';

const INPUT_FNAME = 'input-fname';
const INPUT_FNAME_NODE = `${INPUT_FNAME}-node`;
const INPUT_LNAME = 'input-lname';
const INPUT_LNAME_NODE = `${INPUT_LNAME}-node`;
const INPUT_EMAIL = 'input-email';
const INPUT_EMAIL_NODE = `${INPUT_EMAIL}-node`;
const INPUT_PHONE = 'input-phone';
const INPUT_PHONE_NODE = `${INPUT_PHONE}-node`;
const INPUT_DESCRIPTION = 'input-description';
const INPUT_DESCRIPTION_NODE = `${INPUT_DESCRIPTION}-node`;
const INPUT_BRANCH = 'input-branch';
const INPUT_BRANCH_NODE = `${INPUT_BRANCH}-node`;

const BUTTON_SEND = 'button-send';
const BUTTON_SEND_NODE = `${BUTTON_SEND}-node`;

interface ContactStateLocalValidation {
  fname: boolean;
  lname: boolean;
  email: boolean;
  phone: boolean;
  branchId: boolean;
  description: boolean;
}

interface ContactStateLocal {
  value: {
    fname: string;
    lname: string;
    email: string;
    phone: string;
    branchId: string;
    description: string;
  };
  validation: ContactStateLocalValidation;
  interaction: ContactStateLocalValidation;
}

type NameTypes = keyof ContactStateLocal['value'];

const isValid = (v: ContactStateLocalValidation) =>
  v.fname && v.lname && v.email && v.phone && v.branchId && v.description;

const createState = (_value: ValuationState): ContactStateLocal => {
  const value = { ..._value };
  return {
    value,
    validation: {
      fname: validation.fname(value.fname),
      lname: validation.lname(value.lname),
      email: validation.email(value.email),
      phone: validation.phone(value.phone),
      branchId: validation.branchId(value.branchId),
      description: validation.description(value.description),
    },
    interaction: {
      fname: value.fname ? true : false,
      lname: value.lname ? true : false,
      email: value.email ? true : false,
      phone: value.phone ? true : false,
      branchId: false,
      description: value.description ? true : false,
    },
  };
};

interface ContactProps {
  settings: AppProps;
  valuation: Valuation;
  state: ValuationState;
  updateContactSilent: (state: ValuationState) => void;
  onProceed: (state: ValuationState) => void;
}

class Contact extends HtmlNode {
  private readonly props: ContactProps;
  private state: ContactStateLocal;
  private requestError = false;
  private contexts: {
    fname?: InputField;
    lname?: InputField;
    email?: InputField;
    phone?: InputField;
    branchId?: InputField;
    description?: InputTextarea;
    buttonSend?: ButtonArrowRight;
  } = {};

  constructor(element: HTMLElement | null, props: ContactProps) {
    super(element);
    this.props = props;
    this.state = createState(this.props.state);

    this.render();
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;
    const value = currentTarget.value;

    this.state.value[name] = value;
    this.state.validation[name] = validation[name](value);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;

    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](this.state.value[name]);
    this.updateUiError(name);
    this.updateProceedButton();
    this.props.updateContactSilent({
      ...this.props.state,
      ...this.state.value,
    });
  }

  private onChangeSelect(e: Event) {
    const currentTarget = e.currentTarget as HTMLSelectElement;
    const name = currentTarget.name as 'branchId';
    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](this.state.value[name]);
    this.state.value[name] = currentTarget.value;
    this.updateUiError(name);
    this.updateProceedButton();
    this.render();
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      this.onBlur(e);
    }
  }

  private updateUiError(name: NameTypes) {
    this.contexts[name]?.setError(this.state.interaction[name] && !this.state.validation[name]);
  }

  private updateProceedButton() {
    this.contexts.buttonSend?.disabled(!isValid(this.state.validation));
  }

  private async send() {
    this.requestError = false;
    this.render();
    try {
      this.contexts.buttonSend?.loading(true);

      const _valuationState = { ...this.props.state, ...this.state.value };
      const body = createLeadBody(_valuationState, this.props.valuation, this.props.settings);

      await createLead(this.props.settings.url, body);
      this.props.onProceed({ ...this.props.state, ...this.state.value });
    } catch (e) {
      this.contexts.buttonSend?.loading(false);
      this.requestError = true;
      this.render();
    }
  }

  render() {
    const selectedBranchName = this.props.settings.branches.find(
      (x) => x.id === this.state.value.branchId
    )?.name;

    this.node.innerHTML = `
      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h4 class="waykevaluation-heading waykevaluation-heading--3">
          ${i18next.t('contact.heading')}
        </h4>
        <div class="waykevaluation-content">
          <p class="waykevaluation-content__p">${i18next.t('contact.body')}</p>
        </div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">
          ${i18next.t('contact.detailsHeading')}
        </h5>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_FNAME_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_LNAME_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_EMAIL_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_PHONE_NODE}"></div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <hr class="waykevaluation-separator" />
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">
          ${i18next.t('contact.carInformationHeading')}
        </h5>
        ${
          (this.props.settings.branches?.length || 0) > 1
            ? `
              <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_BRANCH_NODE}"></div>
            `
            : ''
        }
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_DESCRIPTION_NODE}"></div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <hr class="waykevaluation-separator" />
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">
          ${i18next.t('contact.nextStep.heading')}
        </h5>
        <div class="waykevaluation-content">
          <p class="waykevaluation-content__p">
            ${i18next.t('contact.nextStep.body')}
          </p>
        </div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        ${
          this.requestError
            ? `
              <div class="waykevaluation-stack waykevaluation-stack--2">
                ${Alert({
                  tone: 'error',
                  children: i18next.t('contact.requestError') || undefined,
                })}
              </div>`
            : ''
        }
        <div class="waykevaluation-stack waykevaluation-stack--2" id="${BUTTON_SEND_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--2">
          <div class="waykevaluation-disclaimer">
            ${i18next.t('contact.disclaimer.body', {
              brandName: i18next.t('global.brandName'),
              branch: selectedBranchName,
              link: `
                <a
                  href="${i18next.t('contact.disclaimer.link.url')}"
                  title="${i18next.t('contact.disclaimer.link.title')}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="waykevaluation-link"
                >
                  ${i18next.t('contact.disclaimer.link.text')}
                </a>
              `,
              interpolation: { escapeValue: false },
            })}
          </div>
        </div>
      </div>
    `;

    this.contexts.fname = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_FNAME_NODE}`),
      {
        title: i18next.t('input.firstName.label'),
        value: this.state.value.fname,
        id: INPUT_FNAME,
        error: this.state.interaction.fname && !this.state.validation.fname,
        errorMessage: i18next.t('input.firstName.error') || undefined,
        name: 'fname',
        autocomplete: 'off',
        placeholder: i18next.t('input.firstName.placeholder') || undefined,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.lname = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_LNAME_NODE}`),
      {
        title: i18next.t('input.lastName.label'),
        value: this.state.value.lname,
        id: INPUT_LNAME,
        error: this.state.interaction.lname && !this.state.validation.lname,
        errorMessage: i18next.t('input.lastName.error') || undefined,
        name: 'lname',
        autocomplete: 'off',
        placeholder: i18next.t('input.lastName.placeholder') || undefined,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.email = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_EMAIL_NODE}`),
      {
        title: i18next.t('input.email.label'),
        value: this.state.value.email,
        id: INPUT_EMAIL,
        error: this.state.interaction.email && !this.state.validation.email,
        errorMessage: i18next.t('input.email.error') || undefined,
        name: 'email',
        autocomplete: 'off',
        placeholder: i18next.t('input.email.placeholder') || undefined,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.phone = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_PHONE_NODE}`),
      {
        title: i18next.t('input.phone.label'),
        value: this.state.value.phone,
        id: INPUT_PHONE,
        error: this.state.interaction.phone && !this.state.validation.phone,
        errorMessage: i18next.t('input.phone.error') || undefined,
        name: 'phone',
        autocomplete: 'off',
        placeholder: i18next.t('input.phone.placeholder') || undefined,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    if ((this.props.settings.branches.length || 0) > 1) {
      new InputSelect(this.node.querySelector<HTMLDivElement>(`#${INPUT_BRANCH_NODE}`), {
        id: INPUT_BRANCH,
        title: i18next.t('input.branch.label'),
        name: 'branchId',
        value: this.state.value.branchId,
        options: this.props.settings.branches.map((b) => ({ value: b.id, title: b.name })),
        placeholder: i18next.t('input.branch.placeholder') || undefined,
        information: i18next.t('input.branch.help') || undefined,
        onChange: (e) => this.onChangeSelect(e),
      });
    }

    this.contexts.description = new InputTextarea(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_DESCRIPTION_NODE}`),
      {
        title: i18next.t('input.description.label'),
        value: this.state.value.description,
        id: INPUT_DESCRIPTION,
        error: this.state.interaction.description && !this.state.validation.description,
        errorMessage: i18next.t('input.description.error') || undefined,
        name: 'description',
        autocomplete: 'off',
        placeholder: i18next.t('input.description.placeholder') || undefined,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
      }
    );

    this.contexts.buttonSend = new ButtonArrowRight(
      this.node.querySelector<HTMLDivElement>(`#${BUTTON_SEND_NODE}`),
      {
        title: i18next.t('contact.sendFormButtonText'),
        disabled: !isValid(this.state.validation),
        onClick: () => this.send(),
      }
    );
  }
}

export default Contact;
